// src/hooks/useLeadCoaching.ts
import { useCallback, useRef } from "react";
import type { Lead } from "@/types/lead";
import { buildCoachMessage } from "@/lib/lead/coach/coachManager";
import { notifyVoiceFeedbackFirstRun } from "@/lib/voiceFeedback/firstRunNotice";
import { useOrgSettings } from "@/hooks/useOrgSettings";

export type UseLeadCoachingArgs = {
  canSpeakHelp: boolean;
  speak?: (text: string) => void;

  normalizePhone: (s: string | null | undefined) => string;
};

export type UseLeadCoachingResult = {
  coachIfNeeded: (lead: Lead | null) => void;
  resetCoachState: () => void;
};

// Per dev-sync note 6793d1bb — give the user a beat to notice the
// first-run coachmark before the actual coach voice starts speaking.
// 1.4s is long enough for the popover to fade in + register without
// being long enough to feel like a stutter on subsequent leads.
const FIRST_RUN_SPEAK_DELAY_MS = 1400;

export function useLeadCoaching({
  canSpeakHelp,
  speak,
  normalizePhone,
}: UseLeadCoachingArgs): UseLeadCoachingResult {
  const { brandName, organizationName } = useOrgSettings();
  const coachBrandName = brandName.trim() || organizationName.trim() || "your business";
  const lastCoachKeyRef = useRef<string>("");

  const resetCoachState = useCallback(() => {
    lastCoachKeyRef.current = "";
  }, []);

  const coachIfNeeded = useCallback(
    (lead: Lead | null) => {
      if (!canSpeakHelp || !speak) return;

      const { coachKey, message } = buildCoachMessage(lead, { normalizePhone }, coachBrandName);
      if (!message) return;

      if (lastCoachKeyRef.current === coachKey) return;
      lastCoachKeyRef.current = coachKey;

      const firedFirstRun = notifyVoiceFeedbackFirstRun();
      if (firedFirstRun) {
        window.setTimeout(() => speak(message), FIRST_RUN_SPEAK_DELAY_MS);
      } else {
        speak(message);
      }
    },
    [canSpeakHelp, speak, normalizePhone, coachBrandName]
  );

  return { coachIfNeeded, resetCoachState };
}